import React, { useCallback, useContext, useEffect, useRef } from 'react';
import SessionContext from '@providers/Session/SessionContext';
import api from '@services/api';
import rolesConstants from '@utils/rolesConstants';
import { Button, message } from 'antd';
import moment from 'moment';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import { useNavigate } from 'react-router-dom';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const ConsumptionAlertNotify = () => {
  const { t } = useTranslation('alerts');
  const navigate = useNavigate();
  const {
    effectiveRoles,
    userLogged,
    customer: { customerLogged },
    userPreferences: { setUserPreferences },
  } = useContext(SessionContext);

  const hasViewAlertRole = effectiveRoles?.portal?.includes(
    rolesConstants.VIEW_ALERTS,
  );

  const onAlertView = useCallback(() => {
    let productToView = ApplicationsConstants.CONNECT_ONE;

    if (
      customerLogged?.listContractTypes.includes(
        ApplicationsConstants.MULTI_CONNECT.toUpperCase(),
      )
    ) {
      productToView = ApplicationsConstants.MULTI_CONNECT;
    }

    navigate(`/${productToView}/insights/consumption`);
  }, [customerLogged?.listContractTypes, navigate]);

  const getDateFrom = useCallback(() => {
    const storageUser = localStorage.getItem('userPreferences');
    if (storageUser) {
      const userPreferences =
        JSON.parse(storageUser)?.[`user_${userLogged?.user_id}`];
      const storageDateFrom = userPreferences?.consumptionAlerts?.dateFrom;
      if (storageDateFrom) {
        return storageDateFrom;
      }
    }

    const today = moment();
    let previousCycleDateFrom = today;
    if (today.date() !== 23) {
      previousCycleDateFrom = today.clone().subtract(1, 'month').date(23);
    }

    return previousCycleDateFrom.startOf('day').format('YYYY-MM-DD[T]HH:mm:ss');
  }, [userLogged?.user_id]);

  const getAlerts = useCallback(async () => {
    try {
      const dateFrom = getDateFrom();
      const { data } = await api.get(
        `/service-proxy/basic-alerts/summary?dateFrom=${dateFrom}`,
      );

      setUserPreferences({
        consumptionAlerts: {
          dateFrom: moment().format('YYYY-MM-DD[T]HH:mm:ss'),
        },
      });

      if (data?.count > 0) {
        message.open({
          onClose: () => message.destroy(),
          content: (
            <>
              <InfoCircleOutlined />
              <strong>{t('consumption.youHaveNewConsumptionAlerts')}</strong>
              <Button
                type="link"
                size="small"
                onClick={() => {
                  message.destroy();
                  onAlertView();
                }}
              >
                {t('consumption.seeAlerts')}
              </Button>
              <span style={{ margin: 8, opacity: 0.5 }}>|</span>
              <Button
                type="link"
                size="small"
                icon={<CloseOutlined />}
                style={{ marginLeft: 8 }}
                onClick={() => message.destroy()}
              />
            </>
          ),
          duration: 0,
        });
      }
    } catch {
      console.error('error');
    }
  }, [getDateFrom, onAlertView, setUserPreferences, t]);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!hasViewAlertRole) return;

    if (!intervalRef.current) {
      getAlerts();

      const fiveMinutes = 5 * 60 * 1000;
      intervalRef.current = setInterval(getAlerts, fiveMinutes);
    }
  }, [getAlerts, hasViewAlertRole]);

  return null;
};

export default ConsumptionAlertNotify;
